import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { TERMS, PRIVACY } from "@/data/legal";

const linkify = (t) => t.split(/(\S+@lexversant\.ca)/g).map((s, i) => (/@lexversant\.ca$/.test(s) ? <a key={i} href={`mailto:${s}`} className="font-medium text-indigo-600 hover:underline">{s}</a> : s));

export const LegalBody = ({ doc, testId }) => (
  <article data-testid={testId} className="space-y-7 text-[15px] leading-relaxed text-slate-700">
    {doc.sections.map(([h, paras]) => (
      <section key={h} id={h.split(".")[0]} className="scroll-mt-24">
        <h2 className="mb-2 font-display text-lg font-bold text-slate-900">{h}</h2>
        <div className="space-y-2.5">
          {paras.map((p, i) => (Array.isArray(p)
            ? <p key={i} className="border-l-2 border-indigo-200 pl-3"><strong className="text-slate-900">{p[0]}</strong> {linkify(p[1])}</p>
            : <p key={i}>{linkify(p)}</p>))}
        </div>
      </section>
    ))}
  </article>
);

const LegalPage = ({ doc, testId, other }) => (
  <div className="mx-auto max-w-4xl px-4 py-10 sm:px-8 lg:py-14">
    <p className="font-mono text-xs uppercase tracking-[0.3em] text-indigo-300">Legal</p>
    <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{doc.title}</h1>
    <p className="mt-2 text-sm text-slate-400">{doc.meta}</p>
    <div className="mt-4 flex flex-wrap gap-2 print:hidden">
      <button data-testid={`${testId}-print-btn`} onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-4 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:bg-slate-800"><Printer className="h-3.5 w-3.5" />Print / save as PDF</button>
      <Link to={other[0]} data-testid={`${testId}-other-link`} className="rounded-full border border-slate-700 px-4 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:bg-slate-800">{other[1]}</Link>
    </div>
    <nav className="no-scrollbar mt-6 flex gap-1.5 overflow-x-auto pb-1 print:hidden" aria-label="Sections">
      {doc.sections.map(([h]) => <a key={h} href={`#${h.split(".")[0]}`} className="shrink-0 rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300 transition-colors hover:bg-slate-700 hover:text-white">{h.split(". ")[1]}</a>)}
    </nav>
    <div className="mt-6 rounded-2xl bg-white p-6 shadow-2xl shadow-slate-950/40 sm:p-10"><LegalBody doc={doc} testId={testId} /></div>
  </div>
);

export const Terms = () => <LegalPage doc={TERMS} testId="terms-page" other={["/privacy", "Privacy Policy"]} />;
export const Privacy = () => <LegalPage doc={PRIVACY} testId="privacy-page" other={["/terms", "Terms and Conditions"]} />;

export const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-slate-800 bg-slate-950/60 print:hidden">
    <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between sm:px-8">
      <p>© {new Date().getFullYear()} lexversant.ca · Independent practice platform, not affiliated with Pearson or Versant.</p>
      <nav className="flex flex-wrap gap-x-5 gap-y-2">
        <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-white">Terms and Conditions</Link>
        <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-white">Privacy Policy</Link>
        <button data-testid="footer-cookie-settings-btn" onClick={() => { localStorage.removeItem("lexv_cookie_consent"); window.location.reload(); }} className="transition-colors hover:text-white">Cookie settings</button>
        <Link to="/pricing" data-testid="footer-pricing-link" className="transition-colors hover:text-white">Pricing</Link>
        <Link to="/contact" data-testid="footer-contact-link" className="transition-colors hover:text-white">Contact</Link>
      </nav>
    </div>
  </footer>
);
